import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import './OnboardingGuide.css';

/**
 * OnboardingGuide Component
 * 
 * Provides a step-by-step interactive onboarding experience similar to Slack's user onboarding.
 * Shows contextual tooltips/overlays that guide users through the main features.
 */
const OnboardingGuide = ({onComplete, onSkip}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  // Check if user has already completed onboarding
  useEffect(() => {
    const completed = localStorage.getItem('onboardingCompleted') || localStorage.getItem('onboarding_completed');
    if (completed === 'true') {
      setHasCompletedOnboarding(true);
      setIsVisible(false);
    }
  }, []);

  // Onboarding steps with positioning and content
  const steps = [
    {
      id: 'welcome',
      title: '👋 Welcome to AI Chat!',
      description: 'Mia Flow an AI-powered development platform that helps you build and deploy applications faster.',
      content: 'Let\'s take a quick tour to get you started. This will only take a minute!',
      position: 'center',
      highlightSelector: null,
      action: null,
      skippable: true
    },
    {
      id: 'chat-mode',
      title: '💬 Chat with AI Assistant',
      description: 'Ask questions, request code generation, or get help with your development tasks.',
      content: 'Type your question in the chat input below and the AI will assist you in real-time.',
      position: 'bottom-left',
      highlightSelector: '.chat-input-container',
      action: null,
      skippable: true
    },
    {
      id: 'task-progress',
      title: '📊 Track Progress',
      description: 'See AI task progress in real-time.',
      content: 'When AI is working on complex tasks, a progress panel shows each step. Tasks are marked as not-started, in-progress, or completed.',
      position: 'bottom-right',
      highlightSelector: '.task-panel',
      action: 'show-task-panel',
      skippable: true
    },
    {
      id: 'canvas-mode',
      title: '🎨 Canvas Mode',
      description: 'Switch to Canvas Mode to manage your Mia-Platform projects visually.',
      content: 'Click the "Canvas Mode" button to access project configuration, microservices, and deployments.',
      position: 'top-right',
      highlightSelector: '.canvas-toggle',
      action: 'enable-canvas',
      skippable: true
    },
    {
      id: 'project-selection',
      title: '🏢 Select Your Project',
      description: 'Choose a tenant, project, and revision to start working.',
      content: 'Use the dropdowns at the top to navigate to your project. You can manage multiple projects across different tenants.',
      position: 'top',
      highlightSelector: '.canvas-selectors',
      action: null,
      skippable: true,
      requiresCanvas: true
    },
    {
      id: 'resources-tree',
      title: '📦 Project Resources',
      description: 'View all your project resources in one place.',
      content: 'Browse microservices, endpoints, CRUD collections, and Fast Data resources. Click any resource to view or edit it.',
      position: 'left',
      highlightSelector: '.project-resources-tree',
      action: null,
      skippable: true,
      requiresCanvas: true
    },
    {
      id: 'source-code',
      title: '📝 Edit Source Code',
      description: 'Click on a microservice to view and edit its source code.',
      content: 'The Monaco editor provides syntax highlighting, auto-complete, and real-time diff previews. Changes are tracked automatically.',
      position: 'right',
      highlightSelector: '.repository-tree, .repository-content',
      action: 'show-source-code',
      skippable: true,
      requiresCanvas: true
    },
    {
      id: 'ai-assistance',
      title: '🤖 AI-Powered Development',
      description: 'Let AI help you code faster.',
      content: 'Ask AI to "Add a new endpoint", "Create a CRUD collection", or "Fix this bug". AI generates diffs that you can review and accept.',
      position: 'bottom',
      highlightSelector: '.chat-input-container',
      action: null,
      skippable: true
    },
    {
      id: 'preview-mode',
      title: '👀 Preview Your App',
      description: 'Test your frontend application live.',
      content: 'Start a preview server to see your React/Vue/Node.js app running. Auto-fix feature helps resolve errors automatically.',
      position: 'top',
      highlightSelector: '.preview-button, .frontend-preview-trigger',
      action: null,
      skippable: true
    },
    {
      id: 'deploy',
      title: '🚀 Deploy Your Changes',
      description: 'Deploy to your environments with one click.',
      content: 'Review configuration changes, select your environment, and deploy. Monitor deployment status and logs in real-time.',
      position: 'top-right',
      highlightSelector: '.deploy-button, .push-button',
      action: null,
      skippable: true,
      requiresCanvas: true
    },
    {
      id: 'complete',
      title: '🎉 You\'re All Set!',
      description: 'You\'ve completed the onboarding tour.',
      content: 'Start building amazing applications with AI assistance. You can always access help from the Tools menu or restart this tour.',
      position: 'center',
      highlightSelector: null,
      action: 'complete',
      skippable: false
    }
  ];

  const currentStepData = steps[currentStep];

  // Handle next step
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Execute step action if defined
      const nextStepData = steps[nextStep];
      if (nextStepData.action === 'enable-canvas') {
        // Dispatch custom event to enable canvas mode
        window.dispatchEvent(new CustomEvent('onboarding-action', {
          detail: {action: 'enable-canvas'}
        }));
      }
      
      // Auto-open relevant sections for the next step
      executeStepActions(nextStepData);
    } else {
      handleComplete();
    }
  };
  
  // Execute actions needed for a step (open panels, expand sections, etc.)
  const executeStepActions = (stepData) => {
    if (!stepData) return;
    
    setTimeout(() => {
      // For task-progress step, open the task panel
      if (stepData.id === 'task-progress') {
        window.dispatchEvent(new CustomEvent('onboarding-action', {
          detail: {action: 'show-task-panel'}
        }));
      }
      
      // For canvas-mode step, close task panel and enable canvas
      if (stepData.id === 'canvas-mode') {
        // Close task panel first
        window.dispatchEvent(new CustomEvent('onboarding-action', {
          detail: {action: 'hide-task-panel'}
        }));
        
        // Then enable canvas mode
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('onboarding-action', {
            detail: {action: 'enable-canvas'}
          }));
        }, 200);
      }
      
      // For resources-tree step, ensure resources panel is visible
      if (stepData.id === 'resources-tree') {
        // Ensure Canvas mode is active
        window.dispatchEvent(new CustomEvent('onboarding-action', {
          detail: {action: 'enable-canvas'}
        }));
        
        // Expand resources sections in ProjectResourcesTree
        const sections = document.querySelectorAll('.resource-section-header');
        sections.forEach(section => {
          const parent = section.closest('.resource-section');
          if (parent && !parent.querySelector('.resource-list')) {
            section.click();
          }
        });
      }
      
      // For source-code step, switch to Code tab and show repository tree
      if (stepData.id === 'source-code') {
        // Ensure Canvas mode is active
        window.dispatchEvent(new CustomEvent('onboarding-action', {
          detail: {action: 'enable-canvas'}
        }));
        
        // Click on Code tab if not already active
        const codeTab = document.querySelector('button[type="button"]')?.parentElement?.querySelector('button:nth-child(3)');
        if (codeTab && !codeTab.classList.contains('active')) {
          codeTab.click();
        }
        
        // Try to click the first microservice to show repository tree
        setTimeout(() => {
          const firstService = document.querySelector('.resource-item');
          if (firstService) {
            firstService.click();
          }
        }, 500);
      }
    }, 300); // Small delay to allow DOM to update
  };

  // Handle skip
  const handleSkip = () => {
    setIsVisible(false);
    localStorage.setItem('onboardingCompleted', 'true');
    if (onSkip) {
      onSkip();
    }
  };

  // Handle complete
  const handleComplete = () => {
    setIsVisible(false);
    localStorage.setItem('onboardingCompleted', 'true');
    setHasCompletedOnboarding(true);
    if (onComplete) {
      onComplete();
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      
      // Execute actions for the previous step
      const prevStepData = steps[prevStep];
      executeStepActions(prevStepData);
    }
  };
  
  // Auto-execute actions when step changes (for restart functionality)
  useEffect(() => {
    if (isVisible && currentStepData) {
      executeStepActions(currentStepData);
    }
  }, [currentStep, isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add highlight effect to target element
  useEffect(() => {
    if (!isVisible || !currentStepData.highlightSelector) {
      // If not visible or no selector, clean up ALL highlights
      const allHighlighted = document.querySelectorAll('.onboarding-highlight');
      allHighlighted.forEach(el => {
        el.classList.remove('onboarding-highlight');
      });
      return;
    }

    const elements = document.querySelectorAll(currentStepData.highlightSelector);
    if (elements.length === 0) return;

    // Add highlight class to all matching elements
    elements.forEach(el => {
      el.classList.add('onboarding-highlight');
    });

    // Scroll the first element into view with smooth animation
    const firstElement = elements[0];
    setTimeout(() => {
      firstElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 200);

    // Cleanup: Remove ALL highlights when this effect re-runs or unmounts
    return () => {
      const allHighlighted = document.querySelectorAll('.onboarding-highlight');
      allHighlighted.forEach(el => {
        el.classList.remove('onboarding-highlight');
      });
    };
  }, [currentStep, isVisible, currentStepData.highlightSelector]);

  if (!isVisible || hasCompletedOnboarding) {
    return null;
  }

  // Get position classes
  const getPositionClass = () => {
    switch (currentStepData.position) {
      case 'center':
        return 'onboarding-tooltip-center';
      case 'top':
        return 'onboarding-tooltip-top';
      case 'bottom':
        return 'onboarding-tooltip-bottom';
      case 'left':
        return 'onboarding-tooltip-left';
      case 'right':
        return 'onboarding-tooltip-right';
      case 'top-right':
        return 'onboarding-tooltip-top-right';
      case 'top-left':
        return 'onboarding-tooltip-top-left';
      case 'bottom-right':
        return 'onboarding-tooltip-bottom-right';
      case 'bottom-left':
        return 'onboarding-tooltip-bottom-left';
      default:
        return 'onboarding-tooltip-center';
    }
  };

  return (
    <>
      {/* Overlay backdrop */}
      <div className="onboarding-overlay" />
      
      {/* Tooltip/Modal */}
      <div className={`onboarding-tooltip ${getPositionClass()}`}>
        <div className="onboarding-header">
          <h2 className="onboarding-title">{currentStepData.title}</h2>
          {currentStepData.skippable && (
            <button
              className="onboarding-close"
              onClick={handleSkip}
              title="Skip tour"
              type="button"
            >
              {'✕'}
            </button>
          )}
        </div>
        
        <div className="onboarding-body">
          <p className="onboarding-description">{currentStepData.description}</p>
          <p className="onboarding-content">{currentStepData.content}</p>
        </div>
        
        <div className="onboarding-footer">
          <div className="onboarding-progress">
            <span className="onboarding-step-counter">
              {currentStep + 1}{' / '}{steps.length}
            </span>
            <div className="onboarding-progress-bar">
              {steps.map((_, index) => (
                <div
                  className={`onboarding-progress-dot ${index <= currentStep ? 'active' : ''}`}
                  key={index}
                />
              ))}
            </div>
          </div>
          
          <div className="onboarding-actions">
            {currentStep > 0 && (
              <button
                className="onboarding-btn onboarding-btn-secondary"
                onClick={handlePrevious}
                type="button"
              >
                {'Previous'}
              </button>
            )}
            
            {currentStepData.skippable && currentStep < steps.length - 1 && (
              <button
                className="onboarding-btn onboarding-btn-text"
                onClick={handleSkip}
                type="button"
              >
                {'Click here to skip'}
              </button>
            )}
            
            <button
              className="onboarding-btn onboarding-btn-primary"
              onClick={handleNext}
              type="button"
            >
              {currentStep === steps.length - 1 ? 'Get Started' : 'Next Tip'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

OnboardingGuide.propTypes = {
  onComplete: PropTypes.func,
  onSkip: PropTypes.func
};

OnboardingGuide.defaultProps = {
  onComplete: null,
  onSkip: null
};

export default OnboardingGuide;
